'use client';

import React, { useMemo, useEffect } from 'react';
import { Box, Fade, Typography, Paper } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { useEstimator } from '../../context/EstimatorContext';

const initialGridData = [
  { no: 1, account: '141-01-1001', description: 'Land Owner Payments', totalPrice: '' },
  { no: 2, account: '141-01-1003', description: 'Right of Way Agent Fees', totalPrice: '' },
  { no: 3, account: '141-01-1004', description: 'Survey', totalPrice: '' },
  { no: 4, account: '141-01-1007', description: 'Miscellaneous Fees & Permits', totalPrice: '' },
  { no: 5, description: 'ROW Subtotal', isSummary: true, totalPrice: '' },

  { no: 6, account: '141-01-2001', description: 'Line Pipe and Bore Pipe', totalPrice: '' },
  { no: 7, account: '141-01-2002', description: 'Pipe Valves and Fittings', totalPrice: '' },
  { no: 8, account: '141-01-2022', description: 'Cathodic Protection', totalPrice: '' },
  { no: 9, account: '141-01-2023', description: 'Materials Miscellaneous', totalPrice: '' },
  { no: 10, account: '141-01-2041', description: 'Initial Fill', totalPrice: '' },
  { no: 11, account: '141-01-2052', description: 'Freight', totalPrice: '' },
  { no: 12, description: 'Materials Subtotal', isSummary: true, totalPrice: '' },

  { no: 13, account: '141-01-3003', description: 'Construction Labor', totalPrice: '' },
  { no: 14, account: '141-01-3013', description: 'Non-Destructive Examination', totalPrice: '' },
  { no: 15, account: '141-01-4006', description: 'Engineering Services', totalPrice: '' },
  { no: 16, account: '141-01-4007', description: 'EPC Contractors', totalPrice: '' },
  { no: 17, description: 'Construction Subtotal', isSummary: true, totalPrice: '' },

  { no: 18, account: '141-01-4001', description: 'Inspection Services', totalPrice: '' },
  { no: 19, account: '141-01-4002', description: 'Capitalized Salaries & Wages', totalPrice: '' },
  { no: 20, account: '141-01-4003', description: 'Capitalized Travel', totalPrice: '' },
  { no: 21, account: '141-01-4004', description: 'Environmental Services', totalPrice: '' },
  { no: 22, account: '141-01-5001', description: '3rd Party Interconnect Fees', totalPrice: '' },
  { no: 23, account: '141-01-6002', description: 'Legal Fees Services', totalPrice: '' },
  { no: 24, account: '141-01-6009', description: 'Project Overhead (Indirect)', totalPrice: '' },
  { no: 25, description: 'Project Management Subtotal', isSummary: true, totalPrice: '' },

  { no: 26, description: 'Project Base Cost', totalPrice: '' },
  { no: 27, description: 'Contingency', totalPrice: '' },
  { no: 28, description: 'Project Budget', isSummary: true, totalPrice: '' },
];

const getTotalPriceForAccount = (account, costCategory = []) => {
  return costCategory.filter((item) => item.account === account).reduce((sum, item) => sum + (item.price || 0), 0);
};

const ProjectBudgetSummary = ({ onComplete }) => {
  const { formData, setBudgetSummaryRows } = useEstimator(); // include setBudgetSummaryRows

  const gridData = useMemo(() => {
    const costCategory = formData?.costCategory || [];

    const withPrices = initialGridData.map((row) => {
      if (!row.account) return row;
      const price = getTotalPriceForAccount(row.account, costCategory);
      return {
        ...row,
        rawPrice: price,
        totalPrice: price ? `$${price.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '',
      };
    });

    const getSubtotal = (start, end) => withPrices.slice(start - 1, end).reduce((sum, r) => sum + (r.rawPrice || 0), 0);

    const getContingency = (rowNums) => {
      return rowNums.reduce((sum, rn) => {
        const match = costCategory.find((c) => c.rowNumber === rn);
        return sum + (match?.contingencyCost || 0);
      }, 0);
    };

    return withPrices.map((row) => {
      if (row.no === 5) {
        const total = getSubtotal(1, 4);
        row.rawPrice = total;
        return { ...row, totalPrice: `$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
      }
      if (row.no === 12) {
        const total = getSubtotal(6, 11);
        row.rawPrice = total;
        return { ...row, totalPrice: `$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
      }
      if (row.no === 17) {
        const total = getSubtotal(13, 16);
        row.rawPrice = total;
        return { ...row, totalPrice: `$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
      }
      if (row.no === 25) {
        const total = getSubtotal(18, 24);
        row.rawPrice = total;
        return { ...row, totalPrice: `$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
      }
      if (row.no === 24) {
        const baseCost = withPrices.find((r) => r.no === 26)?.rawPrice || 0;
        const overheadPercent =
          parseFloat(formData.costAssumptions?.find((assumption) => assumption.type === 'Contingency')?.value) || 0;
        const overheadCost = baseCost * (overheadPercent / 100);
        console.log(
          `💡 Overhead for row 24 → baseCost: ${baseCost}, overhead %: ${overheadPercent}, total: ${overheadCost}`
        );
        row.rawPrice = overheadCost;
        return {
          ...row,
          totalPrice: `$${overheadCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}`,
        };
      }
      if (row.no === 26) {
        const total =
          (withPrices.find((r) => r.no === 5)?.rawPrice || 0) +
          (withPrices.find((r) => r.no === 12)?.rawPrice || 0) +
          (withPrices.find((r) => r.no === 17)?.rawPrice || 0) +
          (withPrices.find((r) => r.no === 25)?.rawPrice || 0);
        row.rawPrice = total;
        return { ...row, totalPrice: `$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
      }
      if (row.no === 27) {
        const contingency = getContingency([14, 29, 51, 55]);
        row.rawPrice = contingency;
        return { ...row, totalPrice: `$${contingency.toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
      }
      if (row.no === 28) {
        const base = withPrices.find((r) => r.no === 26)?.rawPrice || 0;
        const contingency = withPrices.find((r) => r.no === 27)?.rawPrice || 0;
        const total = base + contingency;
        row.rawPrice = total;
        return { ...row, totalPrice: `$${total.toLocaleString(undefined, { minimumFractionDigits: 2 })}` };
      }
      return row;
    });
  }, [formData?.costCategory, formData?.costAssumptions]);

  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Code',
        field: 'account',
        flex: 1,
        cellStyle: { fontWeight: 500, fontSize: '14px' },
      },
      {
        headerName: 'Description',
        field: 'description',
        flex: 2,
        cellStyle: { fontSize: '14px' },
      },
      {
        headerName: 'Cost Estimate',
        field: 'totalPrice',
        editable: false,
        flex: 1,
        cellStyle: { fontSize: '14px' },
      },
    ],
    []
  );

  useEffect(() => {
    setBudgetSummaryRows(gridData);
  }, [gridData, setBudgetSummaryRows]);

  return (
    <Fade in={true}>
      <Paper
        elevation={4}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          p: 0,
          backgroundColor: '#f9f9f9',
          border: '1px solid #e0e0e0',
        }}
      >
        {/* Title */}
        <Box sx={{ backgroundColor: '#223A64', py: 2, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>
            Project Budget
          </Typography>
        </Box>

        {/* Table */}
        <Box
          className="ag-theme-alpine"
          sx={{
            width: '100%',
            '& .ag-root-wrapper': { border: 'none' },
            '& .ag-row': { borderBottom: '1px solid #eee' },
            '& .ag-cell': { display: 'flex', alignItems: 'center' },
          }}
        >
          <AgGridReact
            rowData={gridData}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true, sortable: false, filter: false }}
            domLayout="autoHeight"
            suppressMenuHide
            suppressCellFocus
            suppressMovableColumns
            suppressColumnVirtualisation
            getRowStyle={(params) => {
              if (params.data?.isSummary) {
                return {
                  backgroundColor: '#e3f2fd',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  color: '#1e88e5',
                  borderTop: '2px solid #90caf9',
                  borderBottom: '2px solid #90caf9',
                };
              }
              return {
                fontSize: '14px',
                lineHeight: '1.2',
                padding: '2px 4px',
              };
            }}
          />
        </Box>
      </Paper>
    </Fade>
  );
};

export default ProjectBudgetSummary;
